import React, { useState, useEffect, useLayoutEffect } from 'react';
// 3rd Party Component goes here
import styled from 'styled-components';
import { RESET_PASSWORD_MESSAGES } from 'Utils/Constants';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'Slice/LoginSlice';
import { TooltipPlacement } from 'antd/es/tooltip';

import { selectResetPassword } from 'Selector/LoginSelector';
import { validatePassword } from 'Utils/UtilityFunctions';
import { useLocation } from 'react-router-dom';
import Button from './Button';
import Input from './Input';
// styles goes here
import '../assets/style.css';

import Message from './Message';
import PasswordValid from './PasswordValid';

interface Props {
  setRedirectPath: (url: string) => void;
}

const LoginWrapper = styled.div`
  margin: 0px;
  padding: 0px 60px;
  border-radius: 30px;
  background-color: #fff;
  display: block;
  width: 100%;
  position: relative;
  @media all and (max-width: 1439px), all and (max-height: 749px) {
    padding: 0px 32px;
    border-radius: 21px;
    max-width: 350px;
    min-width: 300px;
  }
  @media all and (max-width: 992px) {
    min-width: 260px;
  }
  h2 {
    color: #424b50;
    font-size: 32px;
    margin: 35px 0 10px 0;
    font-weight: 800;
    text-align: left;
    font-family: "Roboto Slab";
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      font-size: 26px;
      margin: 25px 0 10px 0;
    }
    @media all and (max-width: 992px) {
      font-size: 20px;
      margin: 25px 0 10px 0;
    }
  }
  .padding-right-password {
    padding-right: 2.2em !important;
  }
  label.remember-login {
    color: #7e7e7e;
    font-size: 16px;
    font-weight: 500;
    display: block;
    padding: 25px 0 30px 0;
    margin: 0px;
    font-family: "Roboto";
    line-height: 22px;
    display: inline-block;
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      padding: 16px 0 16px 0;
      font-size: 12px;
    }
  }
  p {
    font-size: 16px;
    color: #5e7079;
    margin: 27px 0;
    font-family: "Roboto";
    font-weight: 600;
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      margin: 16px 0;
      font-size: 12px;
    }
    a {
      color: #1ec252 !important;
      font-weight: 700;
      text-decoration: none;
    }
    .span {
      margin: 0px 5px;
      cursor: pointer;
      color: #1ec252 !important;
      font-weight: 700;
      text-decoration: none;
    }
  }
  button {
    cursor: pointer;
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      font-size: 16px;
      padding: 8px 18px;
    }
    @media all and (max-width: 992px) {
      font-size: 14px;
    }
  }
  label {
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      margin: 16px 0 5px 0;
      font-size: 12px;
    }
  }
  input[type="text"],
  input[type="password"] {
    @media all and (max-width: 1439px), all and (max-height: 749px) {
      font-size: 16px;
      line-height: 20px;
      padding: 8px 18px;
      &.password + span {
        top: 13px;
      }
    }
    @media all and (max-width: 992px) {
      font-size: 12px;
    }
  }
  .g-recaptcha > div {
    height: 85px !important;
    div > iframe {
      height: 85px !important;
    }
  }
  .footer-action-button{
    margin-bottom: 42px;
    margin-top: 25px;
    @media all and (max-width:992px) {
     text-align: center; 
    }
  }
  .password-valid-popover{
      right: 0px;
      top: 0px;
      width:100%;
      text-align:right;
      span{
        width:100%;
        text-align:right;
        display:block;
        
      }
    }
`;

const ResetNewPassword: React.FC<Props> = function ResetNewPassword(props: Props) {
  const { setRedirectPath } = props;
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [key, setKey] = useState('');

  const [passwordError, setPasswordError] = useState<string>('');
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');
  const [showPassword, togglePassword] = useState(false);
  const [showConfirmPassword, toggleConfirmPassword] = useState(false);
  const resetPassword = useSelector(selectResetPassword);
  const [enablePopover, setEnablePopover] = useState(true);
  const [showPasswordValidate, setShowPasswordValidate] = useState(false);

  const { validate } = validatePassword(password);

  // checking the strength
  const strength = Object.values(validate).every((v) => v === true);

  const location = useLocation();
  const dispatch = useDispatch();

  const handleSubmit = async () => {
    dispatch(actions.resetResetPassword(true));

    if (password === '') {
      setEnablePopover(false);
      setPasswordError(RESET_PASSWORD_MESSAGES.PASSWORD_EMPTY);
      return;
    }
    if (confirmPassword === '') {
      setEnablePopover(false);
      setConfirmPasswordError(RESET_PASSWORD_MESSAGES.CONFIRM_PASSWORD_EMPTY);
      return;
    }
    if (password.valueOf() !== confirmPassword.valueOf()) {
      setEnablePopover(false);
      setPasswordError(RESET_PASSWORD_MESSAGES.PASSWORD_MISMATCH);
      return;
    }

    const obj = {
      key,
      password,
    };
    dispatch(actions.resetPassword(obj));
  };
  const changePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEnablePopover(true);
    setPassword(e.target.value);
    if (passwordError !== '') setPasswordError('');
  };
  const changeConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (passwordError !== '') setPasswordError('');
  };

  useEffect(() => {
    if (resetPassword !== undefined && resetPassword.loaded) {
      if (resetPassword.success) {
        setRedirectPath('passwordUpdated');
        dispatch(actions.resetResetPassword(true));
      }
    }
  }, [resetPassword]);

  useLayoutEffect(() => {
    const { search } = location;
    const position = search.search('key') + 3 + 1;// 3 for key occupies 3 indices and 1 because = occupies 1 index
    const k = search.slice(position);
    setKey(k);
  }, []);
  return (
    <LoginWrapper>
      <h2>Reset Password</h2>
      {resetPassword?.error && (
        <Message
          type="error"
          label="Error!"
          message={resetPassword.errorMessage}
          marginBottom=""
        />
      )}
      <div className="" style={{ position: 'relative' }}>
        <Input
          inputValue={password}
          inputClassName="password padding-right-password"
          isPassword
          inputName="Password"
          error={passwordError}
          inputLabel="Password"
          inputType={showPassword ? 'text' : 'password'}
          inputPlaceholder="Enter your password…"
          onInputTogglePassword={() => togglePassword(!showPassword)}
          onInputChange={changePassword}
          onInputFocus={() => setShowPasswordValidate(true)}
          onInputBlur={() => {
            setShowPasswordValidate(false);
          }}
        />
        {enablePopover && (
          <PasswordValid
            open={showPasswordValidate}
            password={password}
            placement={'leftTop' as TooltipPlacement}
          />
        )}
      </div>
      <Input
        inputValue={confirmPassword}
        inputClassName="password padding-right-password"
        isPassword
        inputName="Confirm Password"
        error={confirmPasswordError}
        inputLabel="Confirm Password"
        inputType={showConfirmPassword ? 'text' : 'password'}
        inputPlaceholder="Enter your password…"
        onInputTogglePassword={() => toggleConfirmPassword(!showConfirmPassword)}
        onInputChange={changeConfirmPassword}
      />
      <div className="footer-action-button">
        <Button
          value={
            `${(resetPassword?.loading ?? false) ? 'Updating...' : 'Update'}`
          }
          isDisabled={((resetPassword?.loading || !strength) ?? false)}
          onClickButton={handleSubmit}
        />

      </div>
    </LoginWrapper>
  );
};

export default ResetNewPassword;
